import { AlertCircle, AlertTriangle, ArrowRight, Clock, ClockCheck, PhoneCall01, SearchLg } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import { getBadgeLabelByOrderStatus, getOrderStatusBadgeType } from 'src/constants/common';
import { formatPlace, IPublicTrackingExpired, IPublicTrackingResult, normalizeWalId, WAL_ID_REGEX } from 'src/services/PublicTrackingService';
import { getOS } from 'src/utils/CommonFunctions';

export type PublicTrackingNotice = 'invalid' | 'notFound' | 'expired' | 'error' | 'blocked';

// The company line printed on every customer email (wal-backend-v2 views/emails, layout-notification).
const WAL_PHONE_DISPLAY = '+1 514-534-0114';
const WAL_PHONE_HREF = 'tel:+15145340114';

// Searches on its own once a complete WAL ID has sat in the field this long (Lucas, 2026-10-01: "auto
// search instead of enter after like 3 seconds"). 1.5 s rather than 3: long enough that a pause
// between digits rarely fires on a half-typed ID (every miss counts against the visitor's IP), short
// enough not to feel stuck. Enter still searches at once, and a pasted ID searches straight away.
const AUTO_SEARCH_MS = 1500;

// `as const` keeps the literal keys, which the typed t() requires. No notice = the opening state.
const STATES = {
    help: { title: 'PublicTracking.Help_Title', body: 'PublicTracking.Help_Body', Icon: PhoneCall01 },
    invalid: { title: 'PublicTracking.Invalid_Title', body: 'PublicTracking.Invalid_WAL_ID', Icon: AlertCircle },
    notFound: { title: 'PublicTracking.Not_Found_Title', body: 'PublicTracking.Not_Found_Body', Icon: SearchLg },
    expired: { title: 'PublicTracking.Expired_Title', body: 'PublicTracking.Expired_Body', Icon: ClockCheck },
    blocked: { title: 'PublicTracking.Blocked_Title', body: 'PublicTracking.Blocked_Body', Icon: Clock },
    error: { title: 'PublicTracking.Error_Title', body: 'PublicTracking.Error_Body', Icon: AlertTriangle },
} as const;

interface IProps {
    value: string;
    onChange: (value: string) => void;
    // Resolves true on a hit, so the field blurs only once a shipment was found.
    onSubmit: () => Promise<boolean>;
    notice: PublicTrackingNotice | null;
    /** The 410 body, for the expired message's WAL ID and delivery date. Null in every other state. */
    expired?: IPublicTrackingExpired | null;
    isSearching: boolean;
    /** The match, shown as a result row until the page opens it. Null in every other state. */
    result: IPublicTrackingResult | null;
    /** Opens the match now, ahead of the page's own short dwell. */
    onOpen: () => void;
    /** The transition to the shipment has started — the row says so instead of inviting a click. */
    isOpening: boolean;
}

/**
 * The milestone the row prints, chosen by status: a shipment that has not moved yet is about its
 * pickup, one that is moving or done is about its delivery. `isActual` decides past vs planned
 * wording, so a date never claims to have happened when it is only scheduled.
 */
const useMilestoneLabel = (result: IPublicTrackingResult | null): string | null => {
    const { t } = useTranslation();

    if (!result) return null;

    const isPickupSide = result.orderStatus === 'booked' || result.orderStatus === 'dispatched';
    const milestone = isPickupSide ? result.pickup : result.delivery;

    if (!milestone?.date) return null;

    // Date-only strings (YYYY-MM-DD) from the API — parsed with an explicit format so they are
    // never shifted a day by the browser's timezone.
    const date = moment(milestone.date, 'YYYY-MM-DD').format('MMM D');
    const key = isPickupSide
        ? milestone.isActual
            ? 'PublicTracking.Found_Picked_Up'
            : 'PublicTracking.Found_Pickup'
        : milestone.isActual
        ? 'PublicTracking.Found_Delivered'
        : 'PublicTracking.Found_Eta';

    return t(key, { date }) as string;
};

/**
 * The WAL ID search in the top bar, built like the WALHQ header search drop-down
 * (wal-frontend-v2 GlobalSearchDropdown, branch dhaval/global-search): the TopHeader field 1:1, typed
 * in place, with a white panel dropping down under it. The panel says what happened and every state
 * offers the call to a rep (Lucas, 2026-10-01). A complete WAL ID searches by itself after a short
 * pause, a pasted one at once, and Enter at any time; the shortcut shown (⌘K, or the portal header's
 * Windows key) focuses the field; Escape or a click outside closes the panel. No suggestions.
 */
const PublicTrackingSearch = ({ value, onChange, onSubmit, notice, expired, isSearching, result, onOpen, isOpening }: IProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const milestoneLabel = useMilestoneLabel(result);
    const route = result ? [formatPlace(result.origin), formatPlace(result.destination)] : [null, null];
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Same key the portal TopHeader displays.
    const searchShortcutKey = useMemo(() => (getOS() === 'Windows' ? 'Y' : 'K'), []);
    const state = STATES[notice ?? 'help'];

    // The expired message names the shipment and the day it was delivered. When the order carries no
    // actual delivery time the date would be a guess, so a shorter body without one is used instead.
    const noticeBody = notice === 'expired' && !expired?.deliveredAt ? ('PublicTracking.Expired_Body_No_Date' as const) : state.body;
    const noticeValues =
        notice === 'expired'
            ? {
                  walId: expired?.orderId ?? '',
                  date: expired?.deliveredAt ? moment(expired.deliveredAt, 'YYYY-MM-DD').format('MMM D, YYYY') : '',
              }
            : undefined;
    const StateIcon = state.Icon;
    // The last ID sent, so the pause never searches the same ID twice. Seeded with the field's first
    // value: a deep link (?walId=) is searched by the page on arrival.
    const lastSearchedRef = useRef(normalizeWalId(value));
    const isPastedRef = useRef(false);

    // A failed deep link (?walId=) arrives with a notice and no focus: show it.
    useEffect(() => {
        if (notice) setIsOpen(true);
    }, [notice]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === searchShortcutKey.toLowerCase()) {
                event.preventDefault();
                containerRef.current?.querySelector('input')?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [searchShortcutKey]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const submit = async () => {
        lastSearchedRef.current = normalizeWalId(value);
        setIsOpen(true);

        // A hit keeps the panel open so the match is shown as a result row (client, 2026-10-07:
        // "on the search when id matches should show"). The page closes it by swapping the body to
        // the shipment, which unmounts this search and takes the panel with it.
        if (await onSubmit()) containerRef.current?.querySelector('input')?.blur();
    };

    // Read by the timer below, so it always calls this render's submit (the one holding the
    // current value) without restarting the pause on every render.
    const submitRef = useRef(submit);
    submitRef.current = submit;

    // Only a value that is already a complete WAL ID searches by itself; a half-typed or malformed
    // one waits for Enter, which explains it.
    useEffect(() => {
        const walId = normalizeWalId(value);
        const delay = isPastedRef.current ? 0 : AUTO_SEARCH_MS;
        isPastedRef.current = false;
        if (isSearching || !WAL_ID_REGEX.test(walId) || walId === lastSearchedRef.current) return undefined;

        const timer = window.setTimeout(() => submitRef.current(), delay);

        return () => window.clearTimeout(timer);
    }, [value, isSearching]);

    return (
        <div ref={containerRef} role="search" className="relative flex-1 max-w-[500px]">
            <InputText
                parentClassName="w-full"
                inputName="walId"
                placeholder={t('PublicTracking.Trigger_Placeholder')}
                inputWrapClass="border-gradient-with-button"
                className={`z-[1] pl-[36px] max-sm:pl-[34px] pr-11 max-sm:pr-3 rounded-[10px] border-gray500 hover:border-borderPrimary bg-grayLight900 focus:bg-grayLight900 focus:border-borderPrimary placeholder:text-white800 text-white800 shadow-sm font-normal !bg-transparent !py-[10px] max-sm:!py-2 !h-auto !text-sm uppercase placeholder:normal-case ${
                    notice === 'invalid' ? '!border !border-red-600' : '!border-0'
                }`}
                icon={
                    <>
                        <SearchLg className="absolute -translate-y-2/4 top-2/4 left-3 max-sm:left-2.5 text-borderPrimary h-4 w-4" />
                        {value === '' && (
                            <div className="rounded px-1 py-px bg-white bg-opacity-[0.08] text-utilityGray700 text-xs font-medium absolute -translate-y-2/4 top-2/4 right-3 max-sm:hidden">
                                ⌘{searchShortcutKey}
                            </div>
                        )}
                    </>
                }
                inputType="text"
                value={value}
                onChangeFunc={(event: any) => onChange(event.target.value)}
                onPaste={() => {
                    isPastedRef.current = true;
                }}
                // Selecting the ID on screen lets the next one be typed straight over it.
                onFocus={(event: any) => {
                    event.target.select();
                    setIsOpen(true);
                }}
                handleKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        // Enter on a match that is already shown opens it now rather than searching
                        // the same ID again (which the dedupe below would drop anyway).
                        if (result && !isOpening) onOpen();
                        else submit();
                    } else if (event.key === 'Escape') {
                        setIsOpen(false);
                        (event.target as HTMLInputElement).blur();
                    }
                }}
                isClearable
                searchIconClass="!right-3 text-borderPrimary z-[2]"
                maxLength={20}
                autoFocus={false}
            />

            {isOpen && (
                <div role={notice ? 'alert' : undefined} className="absolute left-0 right-0 top-full mt-1.5 z-[22] rounded-xl border border-utilityGray200 bg-white shadow-md overflow-hidden">
                    {isSearching ? (
                        <div className="flex items-center justify-center gap-x-2 py-6 px-4">
                            <div className="w-4 h-4 border-2 border-utilityGray200 border-t-primary rounded-full animate-spin" />
                            <span className="text-grayLight600 text-sm font-medium">{t('PublicTracking.Searching')}</span>
                        </div>
                    ) : result ? (
                        // The match. One control for the whole row so there is a single thing to click
                        // and a single thing the keyboard reaches; the page opens it on its own shortly
                        // after, and this just gets there first.
                        <ButtonCmp
                            type="button"
                            size="none"
                            className="w-full !justify-start text-left px-4 py-3 hover:bg-gray25 transition-colors !rounded-none !border-0"
                            onClick={onOpen}
                            disabled={isOpening}
                            ariaLabel={t('PublicTracking.Found_Open_Aria', { walId: result.orderId }) as string}
                        >
                            <span className="flex items-center gap-x-3 w-full min-w-0">
                                <span className="flex flex-col min-w-0 flex-1 gap-y-0.5">
                                    <span className="flex items-center gap-x-2 min-w-0">
                                        <span className="text-grayLight900 text-sm font-semibold truncate">{result.orderId}</span>
                                        <BadgeCmp style="modern" type={getOrderStatusBadgeType(result.orderStatus)} mainClassName="flex-none">
                                            {getBadgeLabelByOrderStatus(result.orderStatus)}
                                        </BadgeCmp>
                                    </span>
                                    {/* The route is dropped rather than half-printed when a stop has no city. */}
                                    {route[0] && route[1] && (
                                        <span className="flex items-center gap-x-1.5 min-w-0 text-grayLight600 text-xs">
                                            <span className="truncate">{route[0]}</span>
                                            <ArrowRight className="w-3 h-3 flex-none" />
                                            <span className="truncate">{route[1]}</span>
                                        </span>
                                    )}
                                    {milestoneLabel && <span className="text-grayLight600 text-xs">{milestoneLabel}</span>}
                                </span>
                                <span className="text-primary text-sm font-semibold flex-none">{t(isOpening ? 'PublicTracking.Found_Opening' : 'PublicTracking.Found_Open')}</span>
                            </span>
                        </ButtonCmp>
                    ) : (
                        <div className="flex flex-col items-center text-center py-6 px-4">
                            <div className="w-10 h-10 rounded-full bg-gray50 flex items-center justify-center mb-3">
                                <StateIcon className="w-5 h-5 text-grayLight600" />
                            </div>
                            <p className="text-grayLight900 text-sm font-semibold">{t(state.title)}</p>
                            <p className="text-grayLight600 text-sm mt-1 max-w-[360px]">{t(noticeBody, noticeValues)}</p>
                            <ButtonCmp
                                type="button"
                                className="btn_secondary_black mt-3"
                                icon={<PhoneCall01 className="w-4 h-4" />}
                                onClick={() => {
                                    window.location.href = WAL_PHONE_HREF;
                                }}
                            >
                                {WAL_PHONE_DISPLAY}
                            </ButtonCmp>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PublicTrackingSearch;
